// src/components/messages/FilterConfig.js
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button'; 
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs'; 
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Save, AlertTriangle, Trash2, Download, Upload, MessageSquare, PlusCircle } from 'lucide-react'; 
import { filterConfigService } from '../../services/filterConfigService';

const FilterConfig = () => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Filter configuration state
  const [config, setConfig] = useState({
    enabled: true,
    autoBlock: false,
    notifyAdmins: true,
    thresholds: {
      low: 0.3,
      medium: 0.6,
      high: 0.8
    },
    categories: []
  });

  // Test message state
  const [testMessage, setTestMessage] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);

  // New category dialog state
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    severity: 'medium',
    keywords: []
  });

  // Fetch initial configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        console.log('[FilterConfig] Fetching initial configuration');
        setLoading(true);
        const data = await filterConfigService.getConfig();
        setConfig(data);
      } catch (err) {
        console.error('[FilterConfig] Error fetching configuration:', err);
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Save configuration changes
  const handleSave = async () => {
    console.log('[FilterConfig] Saving configuration:', config);
    setSaving(true);
    setError(null);
    
    try {
      await filterConfigService.updateConfig(config);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('[FilterConfig] Save error:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    try {
      console.log('[FilterConfig] Adding new category:', newCategory);
      const category = await filterConfigService.addCategory(newCategory);
      setConfig(prev => ({
        ...prev,
        categories: [...prev.categories, category]
      }));
      setShowNewCategoryDialog(false);
      setNewCategory({ name: '', severity: 'medium', keywords: [] });
    } catch (err) {
      console.error('[FilterConfig] Add category error:', err);
      setError('Failed to add category');
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    try {
      console.log('[FilterConfig] Deleting category:', categoryId);
      await filterConfigService.deleteCategory(categoryId);
      setConfig(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat.id !== categoryId)
      }));
    } catch (err) {
      console.error('[FilterConfig] Delete category error:', err);
      setError('Failed to delete category');
    }
  };

  // Add keyword to category
  const handleAddKeyword = async (categoryId, keyword) => {
    if (!keyword.trim()) return;

    try {
      console.log('[FilterConfig] Adding keyword:', { categoryId, keyword });
      await filterConfigService.addKeywords(categoryId, [keyword.trim()]);
      setConfig(prev => ({
        ...prev,
        categories: prev.categories.map(cat => {
          if (cat.id === categoryId) {
            return {
              ...cat,
              keywords: [...cat.keywords, keyword.trim()]
            };
          }
          return cat;
        })
      }));
    } catch (err) {
      console.error('[FilterConfig] Add keyword error:', err);
      setError('Failed to add keyword');
    }
  };

  // Remove keyword from category
  const handleRemoveKeyword = async (categoryId, keyword) => {
    try {
      console.log('[FilterConfig] Removing keyword:', { categoryId, keyword });
      await filterConfigService.removeKeywords(categoryId, [keyword]);
      setConfig(prev => ({
        ...prev,
        categories: prev.categories.map(cat => {
          if (cat.id === categoryId) {
            return {
              ...cat,
              keywords: cat.keywords.filter(k => k !== keyword)
            };
          }
          return cat;
        })
      }));
    } catch (err) {
      console.error('[FilterConfig] Remove keyword error:', err);
      setError('Failed to remove keyword');
    }
  };

  // Test message against current filter configuration
  const handleTestMessage = async () => {
    if (!testMessage.trim()) return;

    try {
      console.log('[FilterConfig] Testing message:', testMessage);
      setTesting(true);
      const results = await filterConfigService.testFilter(testMessage);
      setTestResults(results);
    } catch (err) {
      console.error('[FilterConfig] Test error:', err);
      setError('Failed to test message');
    } finally {
      setTesting(false);
    }
  };

  // Handle file import for keywords
  const handleFileImport = async (categoryId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      console.log('[FilterConfig] Importing keywords file:', { categoryId, file });
      const results = await filterConfigService.importKeywords(categoryId, file);
      setConfig(prev => ({
        ...prev,
        categories: prev.categories.map(cat => {
          if (cat.id === categoryId) {
            return {
              ...cat,
              keywords: [...new Set([...cat.keywords, ...results.keywords])]
            };
          }
          return cat;
        })
      }));
    } catch (err) {
      console.error('[FilterConfig] Import error:', err);
      setError('Failed to import keywords');
    }
  };

  // Export keywords from category
  const handleExport = async (categoryId) => {
    try {
      console.log('[FilterConfig] Exporting keywords:', categoryId);
      const blob = await filterConfigService.exportKeywords(categoryId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `keywords-${categoryId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[FilterConfig] Export error:', err);
      setError('Failed to export keywords');
    }
  };

  const handleTabChange = (value) => {
    console.log('[FilterConfig] Tab changed to:', value);
    setActiveTab(value);
  };

  if (loading) {
    return <div className="p-8 text-center">Loading configuration...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Message Filter Configuration</span>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setShowNewCategoryDialog(true)}
            >
              <PlusCircle className="h-4 w-4" />
              Add Category
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="general">General Settings</TabsTrigger>
              <TabsTrigger value="keywords">Keywords & Categories</TabsTrigger>
              <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
              <TabsTrigger value="test">Test Filter</TabsTrigger>
            </TabsList>

            {/* General Settings Tab */}
            <TabsContent value="general" className="space-y-4">
              <div className="flex items-center justify-between py-4">
                <div>
                  <h3 className="font-medium">Enable Message Filtering</h3>
                  <p className="text-sm text-gray-500">
                    Automatically scan and flag messages based on configured rules
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => 
                    setConfig(prev => ({ ...prev, enabled: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-4">
                <div>
                  <h3 className="font-medium">Auto-block High Severity</h3>
                  <p className="text-sm text-gray-500">
                    Automatically block messages that match high severity rules
                  </p>
                </div>
                <Switch
                  checked={config.autoBlock}
                  onCheckedChange={(checked) => 
                    setConfig(prev => ({ ...prev, autoBlock: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between py-4">
                <div>
                  <h3 className="font-medium">Admin Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Send notifications to admins for flagged messages
                  </p>
                </div>
                <Switch
                  checked={config.notifyAdmins}
                  onCheckedChange={(checked) => 
                    setConfig(prev => ({ ...prev, notifyAdmins: checked }))
                  }
                />
              </div>
            </TabsContent>

            {/* Keywords Tab */}
            <TabsContent value="keywords" className="space-y-6">
              {config.categories.map(category => (
                <div key={category.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{category.name}</h3>
                      <p className="text-sm text-gray-500">
                        Severity: {category.severity}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100">
                        <Upload className="h-4 w-4" />
                        Import
                        <input
                          type="file"
                          accept=".csv,.txt"
                          className="hidden"
                          onChange={(e) => handleFileImport(category.id, e)}
                        />
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleExport(category.id)}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </Button>
                      <Switch
                        checked={category.enabled}
                        onCheckedChange={(checked) => 
                          setConfig(prev => ({
                            ...prev,
                            categories: prev.categories.map(cat => 
                              cat.id === category.id 
                                ? { ...cat, enabled: checked }
                                : cat
                            )
                          }))
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new keyword..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddKeyword(category.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        const input = e.target.previousElementSibling;
                        handleAddKeyword(category.id, input.value);
                        input.value = '';
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {category.keywords.map(keyword => (
                      <div
                        key={keyword}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded"
                      >
                        <span className="text-sm">{keyword}</span>
                        <button
                          onClick={() => handleRemoveKeyword(category.id, keyword)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Thresholds Tab */}
            <TabsContent value="thresholds" className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Low Severity Threshold
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.thresholds.low}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      thresholds: {
                        ...prev.thresholds,
                        low: parseFloat(e.target.value)
                      }
                    }))}
                    className="w-full"
                  />
                  <div className="text-sm text-gray-500">
                    Value: {config.thresholds.low}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Medium Severity Threshold
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.thresholds.medium}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      thresholds: {
                        ...prev.thresholds,
                        medium: parseFloat(e.target.value)
                      }
                    }))}
                    className="w-full"
                  />
                  <div className="text-sm text-gray-500">
                    Value: {config.thresholds.medium}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    High Severity Threshold
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.thresholds.high}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      thresholds: {
                        ...prev.thresholds,
                        high: parseFloat(e.target.value)
                      }
                    }))}
                    className="w-full"
                  />
                  <div className="text-sm text-gray-500">
                    Value: {config.thresholds.high}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Test Filter Tab */}
            <TabsContent value="test" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Test Message
                  </label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Enter a message to test against the current filter configuration..."
                      className="flex-1"
                    />
                    <Button
                      onClick={handleTestMessage}
                      disabled={testing || !testMessage.trim()}
                      className="flex items-center gap-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      {testing ? 'Testing...' : 'Test'}
                    </Button>
                  </div>
                </div>

                {testResults && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900">Test Results</h4>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Filter Score</span>
                        <span className="text-sm font-medium text-gray-900">
                          {testResults.score.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Severity</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${testResults.severity === 'high' ? 'bg-red-100 text-red-800' : 
                            testResults.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'}`}>
                          {testResults.severity}
                        </span>
                      </div>
                      {testResults.matches?.length > 0 && (
                        <div>
                          <span className="text-sm text-gray-600">Matched Keywords:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {testResults.matches.map((match, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {match}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="text-sm text-gray-600">
                        Action: {testResults.action}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mt-4 bg-green-50 text-green-800 border-green-200">
              <AlertDescription>Configuration saved successfully</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Category Name
              </label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
                placeholder="Enter category name..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Severity Level
              </label>
              <Select defaultValue={newCategory.severity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem 
                    value="low" 
                    onClick={() => setNewCategory(prev => ({...prev, severity: "low"}))}
                  >
                    Low
                  </SelectItem>
                  <SelectItem 
                    value="medium" 
                    onClick={() => setNewCategory(prev => ({...prev, severity: "medium"}))}
                  >
                    Medium
                  </SelectItem>
                  <SelectItem 
                    value="high" 
                    onClick={() => setNewCategory(prev => ({...prev, severity: "high"}))}
                  >
                    High
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowNewCategoryDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCategory}
              disabled={!newCategory.name.trim()}
            >
              Add Category
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FilterConfig;